import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Loader2, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { TRY } from "@/lib/format";
import { receiptBelgeNo } from "@/lib/receipt";

const Opt = ({ on, onClick, title, hint, testid }) => (
  <button type="button" onClick={onClick} aria-pressed={on} data-testid={testid}
    className={cn("w-full text-left rounded-xl border px-3.5 py-3 transition-colors", on ? "border-primary bg-primary/[0.06] ring-1 ring-primary" : "border-border hover:bg-muted/60")}>
    <span className="flex items-start gap-2.5">
      <span className={cn("mt-0.5 h-4 w-4 rounded-full border-2 grid place-items-center shrink-0", on ? "border-primary" : "border-muted-foreground/40")}>{on && <span className="h-2 w-2 rounded-full bg-primary" />}</span>
      <span className="min-w-0"><span className="block text-sm font-semibold">{title}</span><span className="block text-xs text-muted-foreground mt-0.5">{hint}</span></span>
    </span>
  </button>
);

// Hızlı satış fişi → teklif: fiyat ve iade seçimi sorulur, teklif ekranı dolu açılır (kaydetmeyi kullanıcı yapar)
export function ConvertToQuoteDialog({ sale, onClose }) {
  const navigate = useNavigate();
  const [prices, setPrices] = useState("sale");
  const [returns, setReturns] = useState("deduct");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (sale) { setPrices("sale"); setReturns("deduct"); setBusy(false); } }, [sale]);
  const hasReturns = (sale?.items || []).some((i) => Number(i.returned) > 0);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/quick-sales/${sale.id}/quote-draft`, { params: { prices, returns: hasReturns ? returns : "all" } });
      onClose();
      navigate("/teklifler", { state: { quoteDraft: { ...data, source: receiptBelgeNo(sale) } } });
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <Dialog open={!!sale} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="qs-quote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-primary" />Fişi Teklife Çevir</DialogTitle>
          <DialogDescription data-testid="qs-quote-sale-info">
            {sale && <>{receiptBelgeNo(sale)} · {sale.title || "Perakende müşteri"} · {(sale.items || []).length} kalem · {TRY(sale.grand_total)}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Fiyatlar</div>
            <Opt on={prices === "sale"} onClick={() => setPrices("sale")} testid="qs-quote-prices-sale" title="Fişteki fiyatlar aynen" hint="Liste fiyatı + fişte uygulanan iskonto (fişteki ek indirim genel iskonto olarak)" />
            <Opt on={prices === "list"} onClick={() => setPrices("list")} testid="qs-quote-prices-list" title="Güncel liste fiyatları" hint="Ürün kartındaki bugünkü liste fiyatı, iskonto %0" />
          </div>
          {hasReturns && (
            <div className="space-y-2" data-testid="qs-quote-returns">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">İade edilen kalemler</div>
              <Opt on={returns === "deduct"} onClick={() => setReturns("deduct")} testid="qs-quote-returns-deduct" title="İade düşülsün" hint="İade edilen miktar düşülür, tamamen iade edilen kalem aktarılmaz" />
              <Opt on={returns === "all"} onClick={() => setReturns("all")} testid="qs-quote-returns-all" title="Fişteki tüm miktarlar aynen" hint="İadeler dikkate alınmaz" />
            </div>
          )}
          <p className="flex items-start gap-2 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 mt-px" />
            <span>Teklif ekranı dolu açılır; kontrol edip <b className="text-foreground">Kaydet</b>'e basarsınız. Fiş değişmez.{sale && !sale.title && " Fişte cari yok — teklif ekranında müşteri seçin."}</span>
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="qs-quote-cancel">Vazgeç</Button>
          <Button onClick={go} disabled={busy} data-testid="qs-quote-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}Teklif Ekranını Aç
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
